import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { api, authHeaders, fmtError } from "@/lib/api";
import { DEFAULT_DECISIONS, DEFAULT_STATS, DEFAULT_STAMPS, DEFAULT_FIELD, RESUME_URL } from "@/components/editorial/theme";
import { Field, Input, Textarea, PrimaryBtn, SecondaryBtn, ImageUploadField, FileUploadField } from "./ui";

const t = (k, label, def = "", extra = {}) => ({ k, label, def, type: "text", ...extra });
const ta = (k, label, def = "") => ({ k, label, def, type: "textarea" });

export const FORMS = [
  { group: "Opening", key: "intro", label: "Holder's page (your photo + intro)", help: "The passport page revealed when the cover opens.", fields: [
    { k: "portrait", label: "Your passport photo", type: "image", def: "" },
    t("photoFocus", "Photo focus point (left% top%)", "50% 25%", { hint: "Which part of the photo to centre on, e.g. 50% 25% = your face." }),
    t("photoZoom", "Photo zoom", "1.4", { hint: "1 = whole photo, 2 = twice as close." }),
    t("eyebrow", "Small label", "p.02 · Holder's page"),
    t("nameLine", "First line", "I'm Shruti."),
    t("titleLine", "Second line (italic)", "A brand strategist with a finance brain."),
    t("surname", "Surname", "Aggarwal"), t("givenNames", "Given names", "Shruti"),
    t("profession", "Profession", "Brand strategist"), t("placeOfIssue", "Place of issue", "New York"),
    t("dateOfIssue", "Date of issue", "Dec 2026"), t("validFor", "Valid for", "Brand · Strategy · Search · PMM"),
    ta("body", "Paragraph", "Two years of forensic finance at Stout taught me to read a balance sheet, value a Fortune 500, and trust the data. Now I bring that rigor to brand strategy, search, and the work of making people actually care."),
  ] },
  { group: "Opening", key: "stats", label: "Stats (4 numbers)", help: "Shown under the intro paragraph. Value can include + or other symbols.", fields: [
    { k: "items", label: "Stats", type: "list", def: DEFAULT_STATS, itemFields: [t("value", "Value"), t("label", "Label")] },
  ] },
  { group: "Opening", key: "stamps", label: "Passport stamps", help: "The 4 ink stamps on the facing page (above the contents menu).", fields: [
    { k: "items", label: "Stamps", type: "list", def: DEFAULT_STAMPS, itemFields: [t("top", "Top word"), t("city", "City (big)"), t("bottom", "Bottom word")] },
  ] },
  { group: "Sections", key: "about", label: "01 Backstory heading", help: "Heading above the chapters.", fields: [
    t("eyebrow", "Small label", "Backstory"), t("heading", "Heading", "Five decisions, one direction."), t("aside", "Side note", "Five chapters, in order. Open one to read it."),
  ] },
  { group: "Sections", key: "nextstop", label: "01 Backstory next stop", help: "The playful last stop on the subway map (the '?' stop) and its card.", fields: [
    t("station", "Stop name on the map", "Your company?"), t("heading", "Card heading", "Next stop: your company."), ta("body", "Card message", "This train is bound for Brand Strategy. Doors are open for Brand, Strategy, Search and Product Marketing roles. Hop on, there's a seat by the window."),
    t("primaryLabel", "Main button text", "Hold the doors"), t("secondaryLabel", "Resume button text", "See my ticket (resume)"),
  ] },
  { group: "Sections", key: "decisions", label: "01 Backstory stops", help: "The subway stops. Each stop opens a chapter card with a small photo. Use the arrows to reorder.", fields: [
    { k: "items", label: "Chapters", type: "list", def: DEFAULT_DECISIONS, itemFields: [{ k: "photo", label: "Chapter photo", type: "image" }, t("station", "Station name (on the map)"), t("chapter", "Chapter title"), ta("decision", "The decision"), ta("risk", "The risk"), ta("outcome", "The outcome"), t("lesson", "The lesson (quote)")] },
  ] },
  { group: "Sections", key: "work", label: "02 Decks heading", help: "Projects themselves are edited in the Projects tab.", fields: [
    t("eyebrow", "Small label", "Decks · Selected work"), t("heading", "Heading", "Seven things I've actually made."), t("subline", "Side note", "A hand of seven. Pick a card — any card — to read the brief behind it."),
  ] },
  { group: "Sections", key: "field", label: "03 Events (tags & passes)", help: "Each entry becomes a luggage tag / backstage pass that flips over when scanned. Upload a photo per entry (or use the Images tab). Points and numbers: one per line; numbers start with the figure, e.g. '500 guests'.", fields: [
    t("eyebrow", "Small label", "Events · In the field"), t("heading", "Heading", "In the room where it happens."), t("aside", "Side note", "Luggage tags and backstage passes from fashion week to the UN. Tap one to scan it."),
    { k: "items", label: "Events", type: "list", def: DEFAULT_FIELD, itemFields: [
      { k: "image", label: "Pass photo", type: "image" }, t("short", "Pass name (short, e.g. NYFW)"), t("tier", "Pass tier (e.g. Volunteer)"),
      t("event", "Event / organisation"), t("role", "Your role"), t("date", "Date"), t("place", "Place"), ta("points", "What you did (one per line)"), ta("numbers", "Numbers (one per line, max 2)"),
    ] },
  ] },
  { group: "Sections", key: "social", label: "04 Reels (film strip)", help: "The scrolling film-strip section. Reels themselves (thumbnail, link, views) are edited in the Social tab.", fields: [
    t("eyebrow", "Small label", "Reels · Social & personal brand"), t("heading", "Heading", "Personal brands for real people."), ta("blurb", "Side note", "Content strategy, direction and captions for creators. Hover to pause the reel, click a frame to watch."),
  ] },
  { group: "Sections", key: "writing", label: "05 Shelf heading", help: "Books themselves are edited in the Articles tab.", fields: [
    t("eyebrow", "Small label", "The Shelf · Writing"), t("heading", "Heading", "Browse the ideas, not the blog."), t("subline", "Side note", "A small library of essays on brand, search, and culture."),
  ] },
  { group: "Sections", key: "guestbook", label: "06 Guestbook heading", help: "The 'Stamp my passport' section. Stamps themselves are managed in the Inbox tab.", fields: [
    t("eyebrow", "Small label", "Guestbook · Visitor stamps"), t("heading", "Heading", "Stamp my passport."), ta("blurb", "Side note", "Recruiter, founder, fellow strategist? Leave your company's stamp on my visa page — proof you stopped by."),
  ] },
  { group: "Sections", key: "contact", label: "07 Say hi", help: "Heading, blurb and all your links. The resume here is used by the top-bar Resume button too.", fields: [
    t("eyebrow", "Small label", "Say hi · RSVP"), t("heading", "Heading", "Let's make something good."), ta("blurb", "Paragraph", "Recruiting for Brand, Strategy, Search, or Product Marketing? Building something wonderful? I'd love to hear about it."),
    t("email", "Email", "sa9858@nyu.edu"), t("linkedin", "LinkedIn URL", "https://www.linkedin.com/in/shrutiaggarwal8/"), t("medium", "Medium URL", "https://medium.com/@sa9858"),
    { k: "resume", label: "Resume (upload a new PDF any time)", type: "file", def: RESUME_URL },
    t("instagram", "Instagram URL (optional)", ""), t("website", "Other link URL (optional)", ""), t("websiteLabel", "Other link name (e.g. Behance)", ""),
  ] },
  { group: "Site-wide", key: "seo", label: "Link preview (sharing)", help: "What shows when you send www.byshruti.com in a chat or post it. Apps remember old previews for a few days, so test with www.byshruti.com/?v=2 after changing.", fields: [
    t("title", "Preview title", "Shruti Aggarwal - Portfolio"), ta("description", "Preview description", "I’ve moved cities four times. Each one taught me to read a new market, speak its language, and earn a place in it. Turns out, that’s exactly what great brands do. Welcome aboard."),
    { k: "image", label: "Preview image (best 1200 x 630)", type: "image", def: "/og-card.jpg" },
  ] },
  { group: "Site-wide", key: "nav", label: "Top bar", help: "Name in the top-left + the rotating words under it.", fields: [
    t("logoName", "Name", "Shruti Aggarwal"),
    { k: "taglineWords", label: "Rotating words (one per line)", type: "lines", def: ["brand strategy", "positioning", "consumer psychology", "storytelling", "product marketing"] },
  ] },
  { group: "Site-wide", key: "marquees", label: "Seal + footer line", help: "The spinning seal (bottom-right) and the scrolling line at the very bottom. Separate phrases with ' · '.", fields: [
    t("spinningBadge", "Seal text", "open to work · brand · strategy · search · product"),
    ta("contactFooter", "Footer scrolling line", "sa9858@nyu.edu · NYC-based · I read decks for fun · let's build something obvious in hindsight"),
  ] },
];

const withDefaults = (form, saved = {}) => {
  const out = { ...saved };
  form.fields.forEach((f) => { if (out[f.k] === undefined || out[f.k] === "") out[f.k] = f.def; });
  return out;
};

function ListField({ f, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div className="space-y-3" data-testid={`list-${f.k}`}>
      {items.map((it, i) => (
        <div key={i} className="border border-black/15 p-4 bg-black/[0.02]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-black uppercase tracking-wider">#{i + 1}</span>
            <div className="flex gap-1">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 disabled:opacity-30"><ArrowUp size={14} /></button>
              <button type="button" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1.5 disabled:opacity-30"><ArrowDown size={14} /></button>
              <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1.5 text-red-700" data-testid={`list-${f.k}-remove-${i}`}><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {f.itemFields.map((sf) => (
              <div key={sf.k} className={sf.type === "textarea" || sf.type === "image" ? "md:col-span-2" : ""}>
                {sf.type === "image" ? (
                  <div><span className="block text-xs uppercase font-bold tracking-wider mb-1">{sf.label}</span><ImageUploadField value={it[sf.k]} onChange={(v) => update(i, sf.k, v)} /></div>
                ) : (
                  <Field label={sf.label}>
                    {sf.type === "textarea"
                      ? <Textarea rows={3} value={it[sf.k] || ""} onChange={(e) => update(i, sf.k, e.target.value)} />
                      : <Input value={it[sf.k] ?? ""} onChange={(e) => update(i, sf.k, e.target.value)} />}
                  </Field>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <SecondaryBtn type="button" onClick={() => onChange([...items, {}])} data-testid={`list-${f.k}-add`}><Plus size={14} /> Add</SecondaryBtn>
    </div>
  );
}

function FieldInput({ f, value, onChange }) {
  const tid = `field-${f.k}`;
  if (f.type === "textarea") return <Textarea data-testid={tid} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "image") return <ImageUploadField value={value} onChange={onChange} />;
  if (f.type === "file") return <FileUploadField value={value} onChange={onChange} />;
  if (f.type === "lines") return <Textarea data-testid={tid} rows={5} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} />;
  if (f.type === "list") return <ListField f={f} value={value} onChange={onChange} />;
  return <Input data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

export function ContentTab({ content, reload }) {
  const [key, setKey] = useState(FORMS[0].key);
  const form = FORMS.find((f) => f.key === key);
  const [draft, setDraft] = useState(() => withDefaults(form, content[key]));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(withDefaults(form, content[key])); }, [key, content, form]);

  const save = async () => {
    setBusy(true);
    const value = { ...draft };
    form.fields.filter((f) => f.type === "lines").forEach((f) => { value[f.k] = (value[f.k] || []).map((s) => s.trim()).filter(Boolean); });
    try {
      await axios.put(api(`/api/admin/content/${key}`), { value }, { headers: authHeaders() });
      toast.success(`${form.label} saved`);
      reload();
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  const groups = Array.from(new Set(FORMS.map((f) => f.group)));
  return (
    <div data-testid="tab-content">
      <h2 className="text-3xl font-black mb-1">Site text</h2>
      <p className="text-sm text-black/60 mb-6">Pick a part of the site on the left, edit the fields, hit Save. Changes go live immediately.</p>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <aside className="md:col-span-4 lg:col-span-3">
          {groups.map((g) => (
            <div key={g} className="mb-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-black/45 mb-1 px-1">{g}</p>
              {FORMS.filter((f) => f.group === g).map((f) => (
                <button key={f.key} data-testid={`content-section-${f.key}`} onClick={() => setKey(f.key)} className={`w-full text-left px-3 py-2 text-sm font-bold rounded-md transition-colors ${key === f.key ? "text-white" : "hover:bg-black/5"}`} style={key === f.key ? { background: "#4A0E17" } : {}}>
                  {f.label}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <div className="md:col-span-8 lg:col-span-9 border border-black/15 p-5 md:p-7">
          <h3 className="text-2xl font-black">{form.label}</h3>
          <p className="text-sm text-black/60 mb-6">{form.help}</p>
          <div className="space-y-5">
            {form.fields.map((f) => (
              <div key={f.k}>
                {f.type === "list" ? <p className="block text-xs uppercase font-bold tracking-wider mb-2">{f.label}</p> : null}
                {f.type === "list"
                  ? <FieldInput f={f} value={draft[f.k]} onChange={(v) => setDraft({ ...draft, [f.k]: v })} />
                  : <Field label={f.label} hint={f.hint}><FieldInput f={f} value={draft[f.k]} onChange={(v) => setDraft({ ...draft, [f.k]: v })} /></Field>}
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-7 sticky bottom-0 bg-white py-3">
            <PrimaryBtn onClick={save} disabled={busy} data-testid="content-save"><Save size={14} /> {busy ? "Saving…" : "Save"}</PrimaryBtn>
            <SecondaryBtn onClick={() => setDraft(withDefaults(form, content[key]))}><RefreshCw size={14} /> Undo changes</SecondaryBtn>
          </div>
        </div>
      </div>
    </div>
  );
}
